import { useMemo, useEffect } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFeatureValue } from '@shared/core/featureFlags';
import { shouldParticipateInExperiment } from '@website-editor/utils/ThingsToDo.util';
import { useGuestPageContainerBySlugQuery } from '@graphql/generated';
import { LayoutType, useThingsToDoTelemetry } from '@apps/guest/packages/layout-engine/widgets/Custom/components/ThingToDo/ThingsToDo.telemetry';
// Global set to track logged exposures across all instances
const loggedExposures = new Set<string>();

const EXPERIMENT_SLUG = 'things-to-do';

/**
 * Hook to handle the Things To Do layout experiment V2.
 * Returns the appropriate component layout based on:
 * 1. Whether the event should participate in the experiment (creation date check)
 * 2. The feature flag value for the experiment
 *
 * @returns {object} Object containing layout type and loading state
 */
export const useThingsToDoExperimentV2 = (): { componentLayout: LayoutType; loading: boolean } => {
  const telemetry = useThingsToDoTelemetry();
  const { eventInfo, eventHandle, loading: eventLoading } = useEventInfo();
  const eventId = eventInfo?.eventId || '';
  const { data: pageData, loading: loadingPageContainer } = useGuestPageContainerBySlugQuery({
    variables: {
      payload: {
        eventName: eventHandle || '',
        pageSlug: EXPERIMENT_SLUG,
      },
      isAdminDashboard: true,
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    skip: eventLoading,
  });
  const pageCreatedAt = pageData?.pageContainerBySlug?.createdAt.timestamp || null;

  // Always get the feature flag value, but only use it if should participate
  const featureThingsToDoLayoutVariant = useFeatureValue('websiteThingsToDoLayoutExperiment').value;
  const componentLayout = useMemo(() => {
    // Check if should participate in V2 experiment (handles V1 exclusion logic internally)
    const shouldParticipateInV2 = shouldParticipateInExperiment(pageCreatedAt, 'v2');

    // Only apply experiment layout if should participate
    if (!shouldParticipateInV2) {
      return '';
    }

    // Apply experiment layout based on feature flag
    if (featureThingsToDoLayoutVariant === 'clean') return 'clean';
    if (featureThingsToDoLayoutVariant === 'modal') return 'modal';
    if (featureThingsToDoLayoutVariant === 'links') return 'links';

    return '';
  }, [featureThingsToDoLayoutVariant, pageCreatedAt]);

  // Custom exposure tracking - log when componentLayout value is final (all async resolved)
  useEffect(() => {
    if (!eventLoading && !loadingPageContainer && eventId && componentLayout) {
      const exposureKey = `${eventId}-${componentLayout}`;

      if (!loggedExposures.has(exposureKey)) {
        telemetry.abTestAssignmentThingsToDoLayout(componentLayout);
        loggedExposures.add(exposureKey);
      }
    }
  }, [componentLayout, eventLoading, loadingPageContainer, eventId, telemetry]);

  return { componentLayout, loading: eventLoading || loadingPageContainer };
};
